<!--
# Frontend Vue | Vendor: FairVerify | App: Phoenix | Type: VisitorLeadSettingsTab
# FairVerify Event Solutions (fairverify.de) based on GodMode Core - GodMode.Systems
# Copyright Tim Haymann, FairVerify Event Solutions GmbH.
-->
<template>
  <div class="container py-3 pb-5 d-flex flex-column gap-3">
    <div class="card shadow-sm">
      <div class="card-body">
        <h2 class="h5">{{ t('settings.localDataTitle') }}</h2>
        <p class="text-muted mb-3">{{ t('settings.localDataText') }}</p>
        <button
          class="btn btn-outline-secondary rounded-pill me-2 mb-2"
          type="button"
          :disabled="!storageReady || !hasScan"
          @click="$emit('resync-all')"
        >
          {{ t('settings.resyncAll') }}
        </button>
        <br>
        <button
          class="btn btn-outline-danger rounded-pill"
          type="button"
          @click="$emit('reset-data')"
        >
          {{ t('settings.resetData') }}
        </button>
      </div>
    </div>

    <div v-if="debugEnabled" class="card shadow-sm">
      <div class="card-body">
        <h2 class="h5">{{ t('settings.debugLogin') }}</h2>
        <pre class="mb-0 small">{{ debugScanLoginSummary }}</pre>
      </div>
    </div>

    <div v-if="invalidScans.length > 0" class="card shadow-sm">
      <div class="card-body">
        <h2 class="h5">{{ t('settings.invalidScansTitle') }}</h2>
        <p class="text-muted mb-3">{{ t('settings.invalidScansText', { count: invalidScans.length }) }}</p>
        <button
          class="btn btn-outline-secondary rounded-pill me-2 mb-2"
          type="button"
          @click="showInvalidScans = !showInvalidScans"
        >
          {{ showInvalidScans ? t('actions.hideList') : t('actions.showList') }}
        </button>
        <button
          v-if="showInvalidScans"
          class="btn btn-outline-primary rounded-pill mb-2"
          type="button"
          @click="$emit('copy-invalid-scans')"
        >
          {{ t('actions.copyList') }}
        </button>
        <textarea
          v-if="showInvalidScans"
          class="form-control mt-3"
          rows="8"
          readonly
          :value="invalidScansExport"
        ></textarea>
      </div>
    </div>

    <div v-if="fallbackAvailable" class="card shadow-sm">
      <div class="card-body">
        <h2 class="h5">{{ t('settings.fallbackTitle') }}</h2>
        <p class="text-muted">{{ t('settings.fallbackText') }}</p>

        <div class="form-check form-switch mb-3">
          <input
            id="fallbackAutoEnabled"
            class="form-check-input"
            type="checkbox"
            :checked="fallbackAutoEnabled"
            @change="$emit('update-fallback-auto', $event.target.checked)"
          >
          <label class="form-check-label" for="fallbackAutoEnabled">
            {{ t('settings.fallbackAuto') }}
          </label>
        </div>

        <div class="form-check form-switch">
          <input
            id="fallbackForcedEnabled"
            class="form-check-input"
            type="checkbox"
            :checked="fallbackForcedEnabled"
            @change="$emit('update-fallback-forced', $event.target.checked)"
          >
          <label class="form-check-label" for="fallbackForcedEnabled">
            {{ t('settings.fallbackForced') }}
          </label>
        </div>
      </div>
    </div>

    <div
      class="card shadow-sm"
      role="button"
      tabindex="0"
      @click="$emit('about-tap')"
      @keyup.enter="$emit('about-tap')"
    >
      <div class="card-body">
        <h2 class="h5">{{ t('settings.aboutTitle') }}</h2>
        <p class="text-muted mb-0">
          {{ t('settings.aboutText') }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { translateVisitorLead, translateVisitorLeadError } from '@/components/visitor-lead/i18n.js'

export default {
  name: 'VisitorLeadSettingsTab',
  props: {
    unsyncedCount: { type: Number, default: 0 },
    storageReady: { type: Boolean, default: true },
    hasScan: { type: Boolean, default: false },
    fallbackAvailable: { type: Boolean, default: false },
    fallbackAutoEnabled: { type: Boolean, default: false },
    fallbackForcedEnabled: { type: Boolean, default: false },
    invalidScans: { type: Array, default: () => [] },
    debugEnabled: { type: Boolean, default: false },
    debugScanLoginSummary: { type: String, default: '' },
    locale: { type: String, default: 'en' },
  },
  data() {
    return {
      showInvalidScans: false,
    }
  },
  computed: {
    invalidScansExport() {
      return this.invalidScans.map((scan) => {
        const segments = [scan.code]
        if (scan.errorCode) segments.push(`[${scan.errorCode}]`)
        if (scan.message || scan.errorCode) {
          segments.push(translateVisitorLeadError(scan.errorCode, scan.message, this.locale))
        }
        return segments.join(' ')
      }).join('\n')
    },
  },
  methods: {
    t(key, replacements = {}) {
      return translateVisitorLead(key, replacements, this.locale)
    },
  },
}
</script>
